import client from './client';
import type { PagedResult } from './anbiyam';

export type CollectionScanStatus = 'Uploaded' | 'Processing' | 'ReviewPending' | 'Applied' | 'Failed';
export type ReviewedStatus = 'Pending' | 'Approved' | 'Rejected';
export type MonthValue = 'Paid' | 'Unpaid' | null;

export interface CollectionScan {
  id:            number;
  uploaded_by:   string;
  s3_key:        string;
  status:        CollectionScanStatus;
  error_message: string | null;
  created_at:    string;
  completed_at:  string | null;
}

export interface CollectionScanItem {
  id:                      number;
  scan_id:                 number;
  family_id:               number | null;
  family_code_extracted:   string | null;
  matchedFamilyCode:       string | null;
  matchedFamilyHead:       string | null;
  subscription_year:       number;
  jan_extracted: MonthValue; feb_extracted: MonthValue; mar_extracted: MonthValue; apr_extracted: MonthValue;
  may_extracted: MonthValue; jun_extracted: MonthValue; jul_extracted: MonthValue; aug_extracted: MonthValue;
  sep_extracted: MonthValue; oct_extracted: MonthValue; nov_extracted: MonthValue; dec_extracted: MonthValue;
  jan_amount: number | null; feb_amount: number | null; mar_amount: number | null; apr_amount: number | null;
  may_amount: number | null; jun_amount: number | null; jul_amount: number | null; aug_amount: number | null;
  sep_amount: number | null; oct_amount: number | null; nov_amount: number | null; dec_amount: number | null;
  overall_confidence:      number | null;
  cemetery_text_extracted: string | null;
  reviewed_status:         ReviewedStatus;
  applied_at:              string | null;
}

export const getCollectionScanUploadUrl = async (): Promise<{ uploadUrl: string; s3Key: string }> => {
  const { data } = await client.post<{ data: { uploadUrl: string; s3Key: string } }>('/collection-review/get-upload-url');
  return data.data;
};

/** Uploads directly to S3 using the presigned URL — bytes never pass through our API. */
export const uploadCollectionScanFile = async (uploadUrl: string, file: File): Promise<void> => {
  await fetch(uploadUrl, { method: 'PUT', body: file, headers: { 'Content-Type': 'application/pdf' } });
};

export const registerCollectionScan = async (s3Key: string): Promise<CollectionScan> => {
  const { data } = await client.post<{ data: CollectionScan }>('/collection-review/scans', { s3Key });
  return data.data;
};

export const listCollectionScans = async (params: { page?: number; pageSize?: number } = {}): Promise<PagedResult<CollectionScan>> => {
  const { data } = await client.get<{ data: PagedResult<CollectionScan> }>('/collection-review/scans', { params });
  return data.data;
};

export const getCollectionScan = async (id: number): Promise<CollectionScan> => {
  const { data } = await client.get<{ data: CollectionScan }>(`/collection-review/scans/${id}`);
  return data.data;
};

export const listCollectionScanItems = async (scanId: number): Promise<CollectionScanItem[]> => {
  const { data } = await client.get<{ data: CollectionScanItem[] }>(`/collection-review/scans/${scanId}/items`);
  return data.data;
};

export const updateCollectionScanItem = async (id: number, payload: {
  jan?: MonthValue; feb?: MonthValue; mar?: MonthValue; apr?: MonthValue;
  may?: MonthValue; jun?: MonthValue; jul?: MonthValue; aug?: MonthValue;
  sep?: MonthValue; oct?: MonthValue; nov?: MonthValue; dec?: MonthValue;
  janAmount?: number | null; febAmount?: number | null; marAmount?: number | null; aprAmount?: number | null;
  mayAmount?: number | null; junAmount?: number | null; julAmount?: number | null; augAmount?: number | null;
  sepAmount?: number | null; octAmount?: number | null; novAmount?: number | null; decAmount?: number | null;
  cemeteryText?: string | null;
  reviewedStatus?: ReviewedStatus;
  familyId?: number | null;
}): Promise<CollectionScanItem> => {
  const { data } = await client.put<{ data: CollectionScanItem }>(`/collection-review/items/${id}`, payload);
  return data.data;
};

export const applyCollectionScan = async (scanId: number): Promise<{ itemsApplied: number; monthsApplied: number; cemeteryRowsApplied: number }> => {
  const { data } = await client.post<{ data: { itemsApplied: number; monthsApplied: number; cemeteryRowsApplied: number } }>(`/collection-review/scans/${scanId}/apply`);
  return data.data;
};
